import React, { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const REASONS = [
  ["copyright", "Copyright / protected IP"],
  ["stolen", "Stolen — not the author's work"],
  ["inappropriate", "Inappropriate content"],
  ["spam", "Spam or misleading"],
  ["other", "Other"],
];

export function ReportDialog({ targetType, targetId, targetName, trigger }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("copyright");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rights/report", { target_type: targetType, target_id: targetId, reason, details });
      toast.success(data.message || "Report submitted — thank you!");
      setOpen(false); setDetails("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <span onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`report-trigger-${targetId}`}>
        {trigger || (
          <button className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] p-1.5" title="Report" data-testid={`report-btn-${targetId}`}>
            <Flag className="w-4 h-4" />
          </button>
        )}
      </span>
      <DialogContent data-testid="report-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-[hsl(var(--destructive))]" /> Report “{targetName}”</DialogTitle>
          <DialogDescription>Flag this for our team to review. Reports help keep the marketplace original and safe — misuse may affect your account.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1 rounded-lg" data-testid="report-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map(([k, label]) => <SelectItem key={k} value={k} data-testid={`report-reason-${k}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-[hsl(var(--muted-foreground))]">Details (optional)</label>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500} rows={3} placeholder="Add any context that helps us review…"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm resize-none" data-testid="report-details" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="report-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[hsl(var(--destructive))] hover:opacity-90" data-testid="report-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
